import apiService from './api';
import type { ApiResponse } from '../types';

export interface Role {
  id: number;
  name: string;
  description: string;
  granted_at?: string;
  expires_at?: string;
}

export interface UserWithRoles {
  id: number;
  username: string;
  email: string;
  organisation_id: number;
  organisation_name?: string;
  roles: Role[];
}

/** Australian state/territory codes used for state-based public holidays. */
export type AustralianState = 'ACT' | 'NSW' | 'NT' | 'QLD' | 'SA' | 'TAS' | 'VIC' | 'WA';
export const AUSTRALIAN_STATES: AustralianState[] = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

export interface UserData {
  id: number;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  myob_card_id?: string | null;
  xero_employee_code?: string | null;
  /** The Xero employee this person is matched to on Accounts → Xero, in the Xero organisation connected now. */
  xero_linked_employee_id?: string | null;
  xero_linked_employee_name?: string | null;
  pay_frequency?: 'weekly' | 'fortnightly' | null;
  /** State whose public holidays apply to this employee. null = national/org-wide only. */
  state?: AustralianState | null;
  email: string;
  organisation_id: number;
  organisation_name?: string;
  supervisor_id?: number | null;
  is_active?: boolean;
}

class UserService {
  // Get all users in the caller's ACTIVE organisation.
  //
  // Org-scoped for everyone, super_admin included. Crossing the org boundary is
  // a separate, explicit request (`allOrgs`) so an org-level screen can never
  // render another tenant's staff just because of who happens to be signed in.
  async getUsers(includeInactive = false, allOrgs = false): Promise<ApiResponse<UserData[]>> {
    const params = new URLSearchParams();
    if (includeInactive) params.set('include_inactive', 'true');
    if (allOrgs) params.set('scope', 'all');
    const qs = params.toString();
    return apiService.get(`/users${qs ? `?${qs}` : ''}`);
  }

  // Set a user's active status (admin only)
  async setUserActiveStatus(userId: number, isActive: boolean): Promise<ApiResponse<any>> {
    return apiService.patch(`/users/${userId}/status`, { is_active: isActive });
  }

  // Revoke all authentication tokens for a user (admin only)
  async revokeUserTokens(userId: number): Promise<ApiResponse<void>> {
    return apiService.post(`/users/${userId}/revoke-tokens`, {});
  }

  // Get all available roles
  async getAllRoles(): Promise<ApiResponse<Role[]>> {
    return apiService.get('/users/roles/all');
  }

  // Get user's roles
  async getUserRoles(userId: number): Promise<ApiResponse<Role[]>> {
    return apiService.get(`/users/${userId}/roles`);
  }

  // Assign role to user
  async assignRole(userId: number, roleName: string): Promise<ApiResponse<any>> {
    return apiService.post(`/users/${userId}/roles`, { role_name: roleName });
  }

  // Remove role from user
  async removeRole(userId: number, roleName: string): Promise<ApiResponse<any>> {
    return apiService.delete(`/users/${userId}/roles/${roleName}`);
  }

  // Get the current user's own profile (includes pay_frequency)
  // Backend returns { success, data: { user: {...} } } — unwrap to { success, data: {...} }.
  async getMyProfile(): Promise<ApiResponse<UserData>> {
    const res = await apiService.get<{ user: UserData }>('/users/me/profile');
    if (res.success && res.data && 'user' in res.data) {
      return { ...res, data: res.data.user } as ApiResponse<UserData>;
    }
    return res as unknown as ApiResponse<UserData>;
  }

  // Admin: update a user's payroll fields (names, MYOB card ID, Xero employee code, pay frequency)
  async updateUserProfile(userId: number, data: {
    first_name: string;
    last_name: string;
    myob_card_id: string;
    xero_employee_code: string;
    pay_frequency: string;
    /** '' clears the state (falls back to national/org-wide holidays). */
    state?: string;
  }): Promise<ApiResponse<any>> {
    return apiService.patch(`/users/${userId}/profile`, data);
  }

  // HR seat / super admin: correct someone else's email. Throws with the
  // server's reason when refused (e.g. an administrator's address).
  async adminChangeEmail(userId: number, email: string): Promise<ApiResponse<{ user: { id: number; email: string; email_verified: boolean }; verification_sent: boolean }>> {
    return apiService.patch(`/users/${userId}/email`, { email });
  }
}

export const userService = new UserService();
export default userService;
